import React from 'react'

import { StyledVideoContents } from 'component-library/content/OovvuuVideoRecommendation/OovvuuVideoRecommendation.styled'
import { Video } from 'component-library/content/Video/Video'
import { InlineContent } from 'component-library/cards/InlineContent/InlineContent'

import {
    OovvuuRecommendationsResponse,
    responseIsError,
    OovvuuRecommendationsDTO,
} from 'component-library/content/OovvuuVideoRecommendation/utils/get-oovvuu-recommendations'
import { mapRecomendationToItem } from 'component-library/content/OovvuuVideoRecommendation/utils/map-oovvuu-to-item'
import { PagePosition, PlayerEvent, VideoQueueItemMeta } from 'common/events/video-event-types'
import { find } from 'common/helpers/utils'
import { ConfigurationContext } from 'common/context-providers/configuration-context'
import { VideoOptions } from '../Video/PlayerInterface'
import { CardEvents } from 'common/client-data-types/card-types'
import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { getMetadata } from '../Video/utils/get-metadata'
import { LoggerContext } from 'component-library/diagnostics/LoggerContext'
import { Logger } from 'typescript-log'

export interface Props {
    videoOptions: VideoOptions
    getVideoRecomendations: () => Promise<OovvuuRecommendationsDTO>
    onEvent: (event: PlayerEvent | CardEvents) => void
    position: PagePosition
}

export interface State {
    recomendedVideos: null | OovvuuRecommendationsResponse['article_params']
    error: null | 'oovvuu-error' | 'request-error'
    showVideoComponent: boolean
}

export class OovvuuVideoRecommendation extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            recomendedVideos: null,
            showVideoComponent: false,
            error: null,
        }
    }

    async componentDidMount() {
        // TODO: need to add GA events here? Or are they handled by the HTTP service in get-oovvuu-recomendations
        try {
            const recommendationDTO = await this.props.getVideoRecomendations()
            if (responseIsError(recommendationDTO)) {
                this.setState({ error: 'oovvuu-error' })
                return
            }

            this.setState({ recomendedVideos: recommendationDTO.article_params })
        } catch (e) {
            this.setState({ error: 'request-error' })
        }
    }

    handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
        this.setState({ showVideoComponent: true })
    }

    handleUserInteraction = () => {}

    render() {
        const { videoOptions, onEvent, position } = this.props
        const { recomendedVideos, showVideoComponent, error } = this.state

        // on error or if there are no video recomendation - show nothing
        if (error || (recomendedVideos !== null && !recomendedVideos.length)) {
            return null
        }

        // if recomendedVideos is null, then it is pending the request - show loading card
        if (recomendedVideos === null) {
            return (
                <React.Fragment>
                    <InlineContent
                        item={{ loaded: false }}
                        hasVerticalSpacing
                        fontScale={1.2}
                        fixedRatio={'16:9'}
                        onEvent={onEvent}
                        cardNumber={1}
                        mediaMode="fullHeight"
                        hasPadding={true}
                    />
                </React.Fragment>
            )
        }

        const videoToUse =
            find(recomendedVideos, video => video.position === '4th') || recomendedVideos[0]

        // if there is no usable video in the recomendations - do not show anything
        if (!videoToUse) {
            return null
        }

        const getVideoQueue = async (config: BaseClientConfig, logger: Logger) => ({
            type: 'oovvuu-recommendation' as 'oovvuu-recommendation',
            items: await Promise.all(
                recomendedVideos
                    .filter(reccomendation => reccomendation.video !== videoToUse.video)
                    .map<Promise<VideoQueueItemMeta>>(id => {
                        return getMetadata(id.video, config.brightcoveAccountId, config, logger)
                    })
            ),
        })

        return (
            <LoggerContext.Consumer>
                {logger => (
                    <ConfigurationContext.Consumer>
                        {config => (
                            <React.Fragment>
                                {!showVideoComponent && (
                                    <InlineContent
                                        hideByline
                                        showVideoDuration
                                        hasVerticalSpacing
                                        item={{
                                            loaded: true,
                                            result: mapRecomendationToItem(
                                                videoToUse,
                                                config.brightcoveAccountId
                                            ),
                                        }}
                                        onEvent={onEvent}
                                        onClick={this.handleCardClick}
                                        overrideLayoutMode
                                        layoutMode="landscape"
                                        hasPadding={true}
                                        mediaMode="fullHeight"
                                        fontScale={1.2}
                                        hideTeaser={false}
                                        // oovvuu cards don't have kickers because we don't get that info from
                                        // their api just yet
                                        fixedRatio={'16:9'}
                                        cardNumber={1}
                                    />
                                )}
                                {/* The video component is rendered to the page - just hidden with display: none - this is so that
                            the correct video play rate analytics can be maintained */}
                                <StyledVideoContents showVideo={showVideoComponent}>
                                    <Video
                                        showTitles
                                        pagePosition={position}
                                        playWhenReady={showVideoComponent}
                                        autoplayOptions={false}
                                        autoplayNextOptions={false}
                                        // oovvuu queue uses the default brightcove account
                                        getVideoQueue={() => getVideoQueue(config, logger)}
                                        onEvent={onEvent}
                                        videoOptions={videoOptions}
                                        videoDetails={{
                                            id: videoToUse.video,
                                            accountId: config.brightcoveAccountId,
                                            heading: videoToUse.headline,
                                            videoType: 'vod',
                                        }}
                                        onUserInteraction={this.handleUserInteraction}
                                    />
                                </StyledVideoContents>
                            </React.Fragment>
                        )}
                    </ConfigurationContext.Consumer>
                )}
            </LoggerContext.Consumer>
        )
    }
}
